import AppIcon from '../components/AppIcon';
import React, { useState } from 'react';

const PARTS_OF_SPEECH = [
  { key: 'NOUN', label: 'Noun', icon: 'noun' },
  { key: 'VERB', label: 'Verb', icon: 'verb' },
  { key: 'ADJECTIVE', label: 'Adjective', icon: 'adjective' },
  { key: 'PHRASE', label: 'Phrase', icon: 'phrase' },
  { key: 'OTHER', label: 'Other', icon: 'other' },
];

const LINEAGE_TYPES = [
  { key: 'MATERNAL', label: 'Maternal', icon: 'maternal' },
  { key: 'PATERNAL', label: 'Paternal', icon: 'paternal' },
  { key: 'BOTH', label: 'Both', icon: 'both' },
];

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

// Demo data
const DEMO_TERMS = [
  { id: '1', term: 'zeiv', translation: "aunt (father's older brother's wife)", partOfSpeech: 'NOUN', genderSpecific: true, generationLevel: 'all', lineageSpecific: 'PATERNAL', hasAudio: true, culturalNotes: 'Used to address the wife of one\'s father\'s older brother. An important term showing respect for family hierarchy.' },
  { id: '2', term: 'taaj', translation: 'grandfather (paternal)', partOfSpeech: 'NOUN', genderSpecific: true, generationLevel: 'elder', lineageSpecific: 'PATERNAL', hasAudio: true, culturalNotes: 'Refers specifically to the paternal grandfather. Mien culture distinguishes between paternal and maternal grandparents.' },
  { id: '3', term: 'mienv', translation: 'Mien person / Mien people', partOfSpeech: 'NOUN', genderSpecific: false, generationLevel: 'all', lineageSpecific: 'BOTH', hasAudio: false, culturalNotes: 'The self-designation of the Iu Mien people. This term carries deep cultural identity significance.' },
  { id: '4', term: 'gaeng', translation: 'to celebrate / ceremony', partOfSpeech: 'VERB', genderSpecific: false, generationLevel: 'all', lineageSpecific: 'BOTH', hasAudio: true, culturalNotes: 'Used in context of traditional ceremonies and celebrations, especially during the New Year festival.' },
  { id: '5', term: 'nyaaj', translation: 'grandmother (paternal)', partOfSpeech: 'NOUN', genderSpecific: true, generationLevel: 'elder', lineageSpecific: 'PATERNAL', hasAudio: false, culturalNotes: 'Paternal grandmother. The Mien kinship system has distinct terms for each grandparent based on lineage.' },
  { id: '6', term: 'hmaiv', translation: 'beautiful / good', partOfSpeech: 'ADJECTIVE', genderSpecific: false, generationLevel: 'all', lineageSpecific: 'BOTH', hasAudio: true, culturalNotes: 'A common compliment term. Also used in naming traditions.' },
];

function CulturalPage() {
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [posFilters, setPosFilters] = useState([]);
  const [lineageFilters, setLineageFilters] = useState([]);
  const [genderFilter, setGenderFilter] = useState('ALL');
  const [audioFilter, setAudioFilter] = useState(false);
  const [favorites, setFavorites] = useState(new Set());
  const [alphaJump, setAlphaJump] = useState('');

  const togglePos = (pos) => {
    setPosFilters((prev) => prev.includes(pos) ? prev.filter((p) => p !== pos) : [...prev, pos]);
  };

  const toggleLineage = (l) => {
    setLineageFilters((prev) => prev.includes(l) ? prev.filter((x) => x !== l) : [...prev, l]);
  };

  const toggleFavorite = (id) => {
    setFavorites((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const filtered = DEMO_TERMS.filter((t) => {
    const q = search.toLowerCase();
    const matchesSearch = !search ||
      t.term.toLowerCase().includes(q) ||
      t.translation.toLowerCase().includes(q) ||
      t.culturalNotes.toLowerCase().includes(q);
    const matchesPos = posFilters.length === 0 || posFilters.includes(t.partOfSpeech);
    const matchesLineage = lineageFilters.length === 0 || lineageFilters.includes(t.lineageSpecific);
    const matchesGender = genderFilter === 'ALL' ||
      (genderFilter === 'YES' && t.genderSpecific) ||
      (genderFilter === 'NO' && !t.genderSpecific);
    const matchesAudio = !audioFilter || t.hasAudio;
    const matchesAlpha = !alphaJump || t.term.charAt(0).toUpperCase() === alphaJump;
    return matchesSearch && matchesPos && matchesLineage && matchesGender && matchesAudio && matchesAlpha;
  });

  return (
    <div className="">

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-gray-500">
            {filtered.length} term{filtered.length !== 1 ? 's' : ''}
          </p>
        </div>

        {filtered.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
            <AppIcon name="book" size={60} />
            <h3 className="mt-4 text-xl font-semibold text-gray-900">No terms found</h3>
            <p className="mt-2 text-gray-500">Try adjusting your search or filters.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((term) => (
              <div key={term.id} className="bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md hover:border-indigo-200 transition-all group">
                <a href={`/cultural-terms/${term.id}`} className="block p-5">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold text-gray-900 group-hover:text-indigo-600 transition-colors">{term.term}</h3>
                      <p className="text-sm text-gray-600 mt-1">{term.translation}</p>
                    </div>
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleFavorite(term.id); }}
                      className="ml-2 text-gray-300 hover:text-amber-500 transition-colors shrink-0"
                    >
                      {favorites.has(term.id) ? <AppIcon name="light" size={16} className="text-yellow-500" /> : <AppIcon name="light" size={16} className="text-gray-300" />}
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium">{term.partOfSpeech}</span>
                    {term.genderSpecific && <span className="px-2 py-0.5 bg-pink-50 text-pink-700 rounded text-xs font-medium">Gender-specific</span>}
                    {term.lineageSpecific && term.lineageSpecific !== 'BOTH' && (
                      <span className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded text-xs font-medium">{term.lineageSpecific}</span>
                    )}
                    {term.hasAudio && <span className="px-2 py-0.5 bg-green-50 text-green-700 rounded text-xs font-medium"><AppIcon name="has-audio" size={14} /> Audio</span>}
                  </div>
                  {term.culturalNotes && (
                    <p className="text-xs text-gray-500 mt-3 line-clamp-2">{term.culturalNotes}</p>
                  )}
                </a>
                {term.hasAudio && (
                  <div className="px-5 pb-4">
                    <button className="flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-700 transition-colors">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                      </svg>
                      Play pronunciation
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default CulturalPage;